export const OVERDUE_BUCKETS = [
  { id: "all", label: "All", test: () => true },
  { id: "d120p", label: "> 120 days", test: (d) => d >= 120 },
  { id: "d90_120", label: "90 - 120 days", test: (d) => d >= 90 && d < 120 },
  { id: "d60_90", label: "60 - 90 days", test: (d) => d >= 60 && d < 90 },
  { id: "d30_60", label: "30 - 60 days", test: (d) => d >= 30 && d < 60 },
  { id: "d0_30", label: "0 - 30 days", test: (d) => d >= 0 && d < 30 },
];

export const OVERDUE_SUMMARY_BUCKETS = OVERDUE_BUCKETS.filter(
  (bucket) => bucket.id !== "all"
);

export const filterRowsByOverdueBucket = (rows, bucketId) => {
  const bucket =
    OVERDUE_BUCKETS.find((b) => b.id === bucketId) || OVERDUE_BUCKETS[0];
  return (rows || []).filter((row) =>
    bucket.test(Number(row.overdueDays) || 0)
  );
};

export const buildOverdueSummary = (rows = []) => {
  const summary = {};
  for (const bucket of OVERDUE_SUMMARY_BUCKETS) {
    summary[bucket.id] = { count: 0, amountsByCurrency: {} };
  }
  for (const row of rows) {
    const days = Number(row.overdueDays) || 0;
    const amount = Number(row.netPremium) || 0;
    const currency = row.currency || "HKD";
    for (const bucket of OVERDUE_SUMMARY_BUCKETS) {
      if (bucket.test(days)) {
        summary[bucket.id].count += 1;
        if (amount) {
          summary[bucket.id].amountsByCurrency[currency] =
            (summary[bucket.id].amountsByCurrency[currency] || 0) + amount;
        }
        break;
      }
    }
  }
  return summary;
};

export const formatOverdueBucketAmounts = (amountsByCurrency, formatCurrency) => {
  const entries = Object.entries(amountsByCurrency || {}).filter(
    ([, amount]) => amount
  );
  if (!entries.length) {
    return formatCurrency(0);
  }
  return entries
    .map(([currency, amount]) => formatCurrency(amount, currency))
    .join(" · ");
};

export const isRowInSettlement = (row) =>
  row.matchStatus === "PENDING" || row.matchStatus === "CONFIRM";

export const isRowNotInSettlement = (row) =>
  row.matchStatus === "NOT_IN_SETTLEMENT";

export const isRowSystemMatched = (row) =>
  isRowInSettlement(row) || isRowNotInSettlement(row);

export const isRowAmountMatched = (row) =>
  row.netPremium != null &&
  row.systemSettleAmount != null &&
  Math.abs(row.netPremium - row.systemSettleAmount) <= 0.01;

export const hasPolicyMismatchWarning = (row) =>
  (row.matchWarnings || []).includes("POLICY_MISMATCH");

export const isRowFullyMatched = (row) =>
  isRowInSettlement(row) &&
  isRowAmountMatched(row) &&
  !hasPolicyMismatchWarning(row);

/** @returns {'ok'|'warning'|'error'} */
export const getRowValidationStatus = (row) => {
  if (isRowFullyMatched(row)) return "ok";
  if (row.matchStatus === "NOT_FOUND") return "error";
  if (
    row.matchStatus === "NOT_IN_SETTLEMENT" ||
    row.matchStatus === "AMBIGUOUS" ||
    hasPolicyMismatchWarning(row) ||
    (isRowInSettlement(row) && !isRowAmountMatched(row))
  ) {
    return "warning";
  }
  return "error";
};

export const getRowValidationTooltipKey = (row) => {
  if (isRowFullyMatched(row)) return "giSettlement.crossCheck.check.matched";
  if (row.matchStatus === "NOT_FOUND")
    return "giSettlement.crossCheck.check.notFound";
  if (row.matchStatus === "NOT_IN_SETTLEMENT")
    return "giSettlement.crossCheck.check.notInSettlement";
  if (row.matchStatus === "AMBIGUOUS")
    return "giSettlement.crossCheck.check.ambiguous";
  if (hasPolicyMismatchWarning(row))
    return "giSettlement.crossCheck.check.policyMismatch";
  if (isRowInSettlement(row) && !isRowAmountMatched(row))
    return "giSettlement.crossCheck.check.amountMismatch";
  return "giSettlement.crossCheck.check.notFound";
};

export const formatCandidateDebitNoteCodes = (candidates = []) =>
  (candidates || [])
    .map((candidate) => candidate.code)
    .filter(Boolean)
    .join(" · ");

export const getOverdueColor = (days) => {
  const d = Number(days) || 0;
  if (d < 30) return "green";
  if (d < 60) return "yellow";
  if (d < 90) return "orange";
  if (d < 120) return "red";
  return "grape";
};

export const getOverdueSummaryColor = (bucketId) => {
  switch (bucketId) {
    case "d0_30":
      return "green";
    case "d30_60":
      return "yellow";
    case "d60_90":
      return "orange";
    case "d90_120":
      return "red";
    case "d120p":
      return "grape";
    default:
      return "gray";
  }
};

export const getMatchStatusColor = (status) => {
  switch (status) {
    case "PENDING":
      return "yellow";
    case "CONFIRM":
      return "green";
    case "NOT_IN_SETTLEMENT":
    case "AMBIGUOUS":
      return "orange";
    case "NOT_FOUND":
    default:
      return "red";
  }
};

export const getMatchStatusLabel = (status, t) => {
  const key = `giSettlement.crossCheck.matchStatus.${status || "NOT_FOUND"}`;
  return t ? t(key) : status || "NOT_FOUND";
};

export const MATCH_STATUS_TOOLTIP_DEFAULTS = {
  "giSettlement.crossCheck.summary.matchedPendingHint":
    "Statement row matched to a debit note in the Pending settlement tab",
  "giSettlement.crossCheck.summary.matchedConfirmHint":
    "Statement row matched to a debit note in the Confirm settlement tab",
  "giSettlement.crossCheck.summary.notInSettlementHint":
    "Matching debit note found in the system but not in this settlement batch",
  "giSettlement.crossCheck.summary.notFoundHint":
    "No matching debit note found in the system for this insurer",
  "giSettlement.crossCheck.summary.ambiguousHint":
    "Multiple debit notes matched — cannot auto-link (e.g. multiple endorsements with the same amount)",
  "giSettlement.crossCheck.check.amountMismatch":
    "Policy found but amount does not match",
  "giSettlement.crossCheck.check.policyMismatch":
    "Matched by DN code but policy number does not match",
};

export const getMatchStatusTooltipKey = (status) => {
  switch (status) {
    case "PENDING":
      return "giSettlement.crossCheck.summary.matchedPendingHint";
    case "CONFIRM":
      return "giSettlement.crossCheck.summary.matchedConfirmHint";
    case "NOT_IN_SETTLEMENT":
      return "giSettlement.crossCheck.summary.notInSettlementHint";
    case "AMBIGUOUS":
      return "giSettlement.crossCheck.summary.ambiguousHint";
    case "NOT_FOUND":
    default:
      return "giSettlement.crossCheck.summary.notFoundHint";
  }
};

export const getMatchStatusTooltip = (row, t, formatPaymentStatus) => {
  const status = row?.matchStatus || "NOT_FOUND";
  let label = t(
    getMatchStatusTooltipKey(status),
    MATCH_STATUS_TOOLTIP_DEFAULTS[getMatchStatusTooltipKey(status)]
  );

  if (
    (status === "PENDING" || status === "CONFIRM") &&
    row.systemSettleAmount != null &&
    !isRowAmountMatched(row)
  ) {
    label = `${label}\n${t(
      "giSettlement.crossCheck.check.amountMismatch",
      MATCH_STATUS_TOOLTIP_DEFAULTS["giSettlement.crossCheck.check.amountMismatch"]
    )}`;
  }

  if (hasPolicyMismatchWarning(row)) {
    label = `${label}\n${t(
      "giSettlement.crossCheck.check.policyMismatch",
      MATCH_STATUS_TOOLTIP_DEFAULTS["giSettlement.crossCheck.check.policyMismatch"]
    )}`;
  }

  if (status === "NOT_IN_SETTLEMENT" && row.matchedDebitNoteCode) {
    const paymentLabel = row.systemPaymentStatus
      ? t(row.systemPaymentStatus)
      : null;
    label = paymentLabel
      ? `${label}\n${row.matchedDebitNoteCode} · ${paymentLabel}`
      : `${label}\n${row.matchedDebitNoteCode}`;
  }

  if (
    (status === "PENDING" || status === "CONFIRM" || status === "AMBIGUOUS") &&
    row.matchedDebitNoteCode
  ) {
    label = `${label}\n${row.matchedDebitNoteCode}`;
  }

  if (status === "AMBIGUOUS" && row.candidateDebitNotes?.length) {
    const candidateCodes = formatCandidateDebitNoteCodes(row.candidateDebitNotes);
    if (candidateCodes) {
      label = `${label}\n${t(
        "giSettlement.crossCheck.check.candidateDns",
        "Possible DNs"
      )}: ${candidateCodes}`;
    }
  }

  return label;
};

export const getOverdueSummaryLabelKey = (bucketId) =>
  `giSettlement.crossCheck.summary.${bucketId}`;
